import * as React from 'react';
import { DndContext, DragEndEvent, DragOverlay, useDraggable, useDroppable } from '@dnd-kit/core';
import { Profile, ProfileType, HcpGroup } from '@watcherr/resources';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '@watcherr/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';

export const HcpAssignmentTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { notifications } = useStores();
  const [activeDragId, setActiveDragId] = React.useState<string | null>(null);

  // Fetch all groups
  const { data: groups } = useQuery({
    queryKey: ['hcpGroups'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  // Fetch all HCPs
  const { data: allHcps } = useQuery({
    queryKey: ['hcps'],
    queryFn: () => Profile.getProfiles({ profileType: ProfileType.HealthcareProvider }),
  });

  // Mutation to assign HCP to group (automatically removes from other groups)
  const assignMutation = useMutation({
    mutationFn: ({ groupId, hcpId }: { groupId: string; hcpId: number }) =>
      HcpGroup.assignHcpToHcpGroup(groupId, hcpId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hcpGroups'] });
      queryClient.invalidateQueries({ queryKey: ['hcps'] });
      notifications.addUserActionNotification({
        type: 'success' as any,
        notificationType: 'success',
        message: t('hcpGroups.assign.success'),
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const hcpId = parseInt(active.id as string);
    const targetId = over.id as string;

    if (targetId.startsWith('group-')) {
      const groupId = targetId.replace('group-', '');
      assignMutation.mutate({ groupId, hcpId });
    }
  };

  // Get HCPs assigned to each group
  const getGroupHcps = (groupId: string) => {
    if (!allHcps) return [];
    return allHcps.filter((hcp) => hcp.hcpGroupId === groupId);
  };

  // Get unassigned HCPs (those without a group)
  const unassignedHcps = React.useMemo(() => {
    if (!allHcps) return [];
    return allHcps.filter((hcp) => !hcp.hcpGroupId);
  }, [allHcps]);

  return (
    <DndContext onDragEnd={handleDragEnd} onDragStart={(e) => setActiveDragId(e.active.id as string)}>
      <div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
              <Table.HeadCol>{t('hcpGroups.assigned.hcps')}</Table.HeadCol>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {Array.isArray(groups) &&
              groups.map((group) => (
                <Table.Row key={group.id}>
                  <Table.Col>{group.hcpGroupName}</Table.Col>
                  <Table.Col>
                    <GroupDropZone groupId={group.id} assignedHcps={getGroupHcps(group.id)} />
                  </Table.Col>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>

        <div style={{ marginTop: '30px', padding: '20px', border: '1px dashed #ccc', borderRadius: '4px' }}>
          <h4>{t('hcpGroups.unassigned.hcps')}</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '15px' }}>
            {unassignedHcps.map((hcp) => (
              <DraggableHcp key={hcp.id} hcp={hcp} />
            ))}
          </div>
        </div>
      </div>
      <DragOverlay>
        {activeDragId && (
          <div
            style={{
              padding: '8px 16px',
              background: '#fff',
              border: '2px solid #4CAF50',
              borderRadius: '16px',
              whiteSpace: 'nowrap',
              minWidth: 'fit-content',
            }}
          >
            {allHcps?.find((h) => h.id === parseInt(activeDragId))?.firstName}{' '}
            {allHcps?.find((h) => h.id === parseInt(activeDragId))?.lastName}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

// Droppable group zone
function GroupDropZone({ groupId, assignedHcps }: { groupId: string; assignedHcps: any[] }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `group-${groupId}`,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        minHeight: '40px',
        padding: '10px',
        border: `2px ${isOver ? 'solid' : 'dashed'} ${isOver ? '#4CAF50' : '#ccc'}`,
        borderRadius: '4px',
        backgroundColor: isOver ? '#e8f5e9' : 'transparent',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      {assignedHcps.length === 0 ? (
        <span style={{ color: '#999' }}>{t('hcpGroups.dropzone.hcps')}</span>
      ) : (
        assignedHcps.map((hcp) => <DraggableHcp key={hcp.id} hcp={hcp} />)
      )}
    </div>
  );
}

// Draggable HCP chip
function DraggableHcp({ hcp }: { hcp: any }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: hcp.id.toString(),
  });

  const style: React.CSSProperties = {
    padding: '8px 16px',
    background: isDragging ? '#c8e6c9' : '#e3f2fd',
    border: '1px solid #90caf9',
    borderRadius: '12px',
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {hcp.firstName} {hcp.lastName}
    </div>
  );
}
